import React from 'react'
import { Box, Typography } from '@mui/material'
import type { ThemeAIGroup, ThemeAITheme } from '../../types/gotypes'

interface FrameThemeListProps {
  themes: ThemeAITheme[]
  groups?: ThemeAIGroup[]
}

const ThemeRow: React.FC<{ theme: ThemeAITheme }> = ({ theme }) => (
  <Box
    component="li"
    sx={{ display: 'grid', gridTemplateColumns: '32px 1fr', columnGap: 1.5, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', pt: '1px' }}>
      {theme.code}
    </Typography>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{theme.name}</Typography>
      {theme.assignment_explanation && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '75ch', mt: 0.25 }}>
          {theme.assignment_explanation}
        </Typography>
      )}
    </Box>
  </Box>
)

// The code frame the task will code with, as a reading list: code, theme and
// the rule for when to assign it. Grouped when the frame has groups.
const FrameThemeList: React.FC<FrameThemeListProps> = ({ themes, groups }) => {
  const ordered = [...themes].sort((a, b) => a.order - b.order || a.code - b.code)

  if (!groups || groups.length === 0) {
    return (
      <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {ordered.map((theme) => <ThemeRow key={theme.id} theme={theme} />)}
      </Box>
    )
  }

  const sortedGroups = [...groups].sort((a, b) => a.order - b.order)
  const ungrouped = ordered.filter((theme) => !theme.group_id || !groups.some((g) => g.id === theme.group_id))
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sortedGroups.map((group) => (
        <Box key={group.id}>
          <Typography variant="overline" color="text.secondary">{group.name}</Typography>
          <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {ordered.filter((theme) => theme.group_id === group.id).map((theme) => <ThemeRow key={theme.id} theme={theme} />)}
          </Box>
        </Box>
      ))}
      {ungrouped.length > 0 && (
        <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {ungrouped.map((theme) => <ThemeRow key={theme.id} theme={theme} />)}
        </Box>
      )}
    </Box>
  )
}

export default FrameThemeList
